import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FaArrowLeft,
  FaBriefcase,
  FaBuilding,
  FaCamera,
  FaEnvelope,
  FaExclamationCircle,
  FaIdBadge,
  FaLock,
  FaMapMarkerAlt,
  FaPen,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import "../assets/css/MyDetails.css";
import "../assets/css/Permissions.css";
import axiosInstance from "../components/api/axiosInstance";
import { getApiUrl, resolveImageUrl } from "../config/apiConfig";
import { getCurrentUserRole, getStoredRolePermissions } from "../utils/roleAccess";
import { getRolePermissions } from "./rolePermissionService";
import { getAuthItem, setAuthItem } from "../utils/authStorage";
import { formatPascal, applyPascalNameFields } from "../utils/formatters";
import { formatPhoneDisplay, formValueWithMobileSanitize, mobileValidationMessage, phoneCallLink } from "../utils/phone";

function groupPermsByModule(perms) {
  if (!Array.isArray(perms) || perms.length === 0) return null;
  return perms.reduce((acc, p) => {
    const key = (p.Module_Name || p.module_name || p.Module || p.module || "Other").trim() || "Other";
    if (!acc[key]) acc[key] = [];
    const name = p.Permission_Name || p.permission_name || p.name || `#${p.Permission_Id ?? p.permission_id ?? p.id ?? ""}`;
    acc[key].push(name);
    return acc;
  }, {});
}

const ACTION_TONES = {
  create: "add",
  add: "add",
  edit: "edit",
  update: "edit",
  delete: "delete",
  remove: "delete",
  view: "view",
  read: "view",
};

function permissionChip(name) {
  const text = String(name || "").trim();
  const [first, ...rest] = text.split(/\s+/);
  const tone = ACTION_TONES[(first || "").toLowerCase()];
  return { tone: tone || "other", label: tone && rest.length ? first : text, title: text };
}

const STATUS_TONES = {
  active: "success",
  inactive: "neutral",
  resigned: "warning",
  terminated: "danger",
  "on hold": "warning",
};

function initialsOf(first, last) {
  const a = String(first || "").trim()[0] || "";
  const b = String(last || "").trim()[0] || "";
  return (a + b).toUpperCase() || "U";
}

function SectionCard({ icon: Icon, title, subtitle, aside, children }) {
  return (
    <section className="md-card">
      <header className="md-card__head">
        <span className="md-card__icon" aria-hidden="true">
          <Icon />
        </span>
        <div className="md-card__titles">
          <h2 className="md-card__title">{title}</h2>
          {subtitle ? <p className="md-card__subtitle">{subtitle}</p> : null}
        </div>
        {aside ? <div className="md-card__aside">{aside}</div> : null}
      </header>
      <div className="md-card__body">{children}</div>
    </section>
  );
}

function formatDisplayDate(value) {
  if (!value) return "";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function maskAadhaar(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 4) return value || "";
  return `XXXX XXXX ${digits.slice(-4)}`;
}

function Info({ label, value, href, full, children }) {
  const empty = children == null && (value == null || String(value).trim() === "");
  return (
    <div className={`md-info${full ? " md-info--full" : ""}`}>
      <span className="md-info__label">{label}</span>
      {children != null ? (
        <span className="md-info__value">{children}</span>
      ) : empty ? (
        <span className="md-info__value is-empty">—</span>
      ) : href ? (
        <a className="md-info__value is-link" href={href}>
          {value}
        </a>
      ) : (
        <span className="md-info__value">{value}</span>
      )}
    </div>
  );
}

function ViewCard({ icon: Icon, tone = "blue", title, action, wide, children }) {
  return (
    <section className={`md-vcard${wide ? " md-vcard--wide" : ""}`}>
      <div className="md-vcard__head">
        <h3 className="md-vcard__title">
          <span className={`md-vcard__icon md-vcard__icon--${tone}`} aria-hidden="true">
            <Icon />
          </span>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, full, children }) {
  return (
    <label className={`md-field${full ? " md-field--full" : ""}`}>
      <span className="md-field__label">
        {label}
        {required ? <span className="md-field__req"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function MyDetails({ onBack, permissionsVersion = 0 }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    phoneNumber: "",
    emergencyContactNumber: "",
    aadhaar: "",
    pan: "",
    address: "",
    company: "",
    companyEmail: "",
    role: "",
    department: "",
    employeeId: "",
    band: "",
    dateOfJoining: "",
    dateOfLeaving: "",
    status: "",
    companyLocation: "",
    profileImage: null,
    currentImageUrl: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const userRole = getCurrentUserRole();
  const canEditProfessional =
    userRole === "admin" || userRole === "hr";

  const [assignedModulesAndPerms, setAssignedModulesAndPerms] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [permQuery, setPermQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const editSnapshotRef = useRef(null);

  const startEditing = () => {
    editSnapshotRef.current = formData;
    setError("");
    setSuccess("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (editSnapshotRef.current) setFormData({ ...editSnapshotRef.current, profileImage: null });
    setError("");
    setIsEditing(false);
  };
  const [previewUrl, setPreviewUrl] = useState("");
  const topRef = useRef(null);

  useEffect(() => {
    if (!formData.profileImage) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(formData.profileImage);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [formData.profileImage]);

  useEffect(() => {
    if (error || success) topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [error, success]);

  const permissionModules = useMemo(() => {
    if (!assignedModulesAndPerms) return [];
    return Object.keys(assignedModulesAndPerms)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({
        name,
        perms: assignedModulesAndPerms[name].slice().sort((a, b) => a.localeCompare(b)),
      }));
  }, [assignedModulesAndPerms]);

  const totalPermissions = useMemo(
    () => permissionModules.reduce((n, m) => n + m.perms.length, 0),
    [permissionModules]
  );

  const visibleModules = useMemo(() => {
    const q = permQuery.trim().toLowerCase();
    if (!q) return permissionModules;
    return permissionModules.filter(
      (m) => m.name.toLowerCase().includes(q) || m.perms.some((p) => String(p).toLowerCase().includes(q))
    );
  }, [permissionModules, permQuery]);

  useEffect(() => {
    let mounted = true;
    const loadPermissions = async () => {
      const userStr = getAuthItem("user");
      let roleCount = 0;
      try {
        const u = userStr ? JSON.parse(userStr) : null;
        roleCount = Array.isArray(u?.roles) ? u.roles.length : 0;
      } catch (_) {}
      // Multi-role users: sign-in already stored the merged permissions of all roles.
      if (roleCount > 1) {
        setAssignedModulesAndPerms(groupPermsByModule(getStoredRolePermissions()));
        setLoadingPerms(false);
        return;
      }
      let roleId = null;
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          roleId =
            user?.roles?.[0]?.role_id ??
            user?.roles?.[0]?.Role_Id ??
            user?.roles?.[0]?.Designation_Id ??
            user?.roles?.[0]?.designation_id ??
            user?.role_id ??
            user?.Role_Id ??
            user?.roleId ??
            null;
        } catch (_) {}
      }
      const fromApi = roleId != null ? await getRolePermissions(roleId) : { data: [], fetched: false };
      if (!mounted) return;
      if (fromApi.fetched && Array.isArray(fromApi.data) && fromApi.data.length > 0) {
        const grouped = groupPermsByModule(fromApi.data);
        setAssignedModulesAndPerms(grouped);
        try {
          setAuthItem("user_role_permissions", JSON.stringify(fromApi.data));
        } catch (_) {}
      } else {
        const rk = roleId != null ? String(roleId) : "";
        let perms = null;
        try {
          const cacheRaw = localStorage.getItem("role_permissions_cache");
          if (cacheRaw && rk) {
            const cache = JSON.parse(cacheRaw);
            const cachedForRole = cache?.[rk];
            if (Array.isArray(cachedForRole) && cachedForRole.length > 0) {
              perms = cachedForRole.map((p) => ({
                Module_Name: p.module ?? p.Module_Name ?? p.module_name ?? "",
                Permission_Name: p.name ?? p.Permission_Name ?? p.permission_name ?? "",
                Permission_Id: p.id ?? p.Permission_Id ?? p.permission_id,
                module_name: p.module,
                permission_name: p.name,
                permission_id: p.id,
              }));
              try {
                setAuthItem("user_role_permissions", JSON.stringify(perms));
              } catch (_) {}
            }
          }
        } catch (_) {}
        if (!perms) {
          perms = getStoredRolePermissions();
        }
        setAssignedModulesAndPerms(groupPermsByModule(perms));
      }
      setLoadingPerms(false);
    };
    loadPermissions();
    return () => { mounted = false; };
  }, [permissionsVersion]);

  useEffect(() => {
    // Load user data from sessionStorage or API
    const loadUserData = async () => {
      try {
        const userStr = getAuthItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          // Fix image URL if it contains localhost
          const imageUrl = (user.image_url || "").length ? resolveImageUrl(user.image_url) : "";
          
          setFormData({
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            email: user.email || "",
            gender: user.gender || "",
            dateOfBirth: user.date_of_birth || "",
            phoneNumber: user.phone_number || "",
            emergencyContactNumber: user.emergency_contact_number || "",
            aadhaar: user.aadhaar || "",
            pan: user.pan || "",
            address: user.address || "",
            company: user.company || "",
            companyEmail: user.company_email || "",
            role:
              user.designation_name ||
              user.Designation_Name ||
              (Array.isArray(user.roles) && user.roles[0]?.designation_name) ||
              user.role_name ||
              "",
            department: user.department || "",
            employeeId: user.employee_id || "",
            band: user.band || "",
            dateOfJoining: user.doj || "",
            dateOfLeaving: user.date_of_leaving || "",
            status: user.status || "",
            companyLocation: user.company_location || "",
            profileImage: null,
            currentImageUrl: imageUrl
          });
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };

    loadUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file") {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: formValueWithMobileSanitize(name, value)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const userStr = getAuthItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.user_id ?? user?.id;
      if (!userId) {
        setError("User session not found. Please sign in again.");
        setLoading(false);
        return;
      }

      const phoneError = mobileValidationMessage(formData.phoneNumber, { label: "Phone number" });
      if (phoneError) {
        setError(phoneError);
        setLoading(false);
        return;
      }
      const emergencyError = mobileValidationMessage(formData.emergencyContactNumber, { label: "Emergency contact number" });
      if (emergencyError) {
        setError(emergencyError);
        setLoading(false);
        return;
      }

      let response;
      
      // If an image is selected, use FormData for multipart/form-data upload
      if (formData.profileImage) {
        const formDataToSend = new FormData();
        
        // Backend expects key 'image_path' for the file
        formDataToSend.append('image_path', formData.profileImage);
        
        // Append all other fields
        formDataToSend.append('first_name', formatPascal(formData.firstName));
        formDataToSend.append('last_name', formatPascal(formData.lastName));
        formDataToSend.append('email', formData.email);
        formDataToSend.append('company_email', formData.companyEmail);
        
        if (formData.gender) formDataToSend.append('gender', formData.gender);
        if (formData.dateOfBirth) formDataToSend.append('date_of_birth', formData.dateOfBirth);
        if (formData.phoneNumber) formDataToSend.append('phone_number', formData.phoneNumber);
        if (formData.emergencyContactNumber) formDataToSend.append('emergency_contact_number', formData.emergencyContactNumber);
        if (formData.aadhaar) formDataToSend.append('aadhaar', formData.aadhaar);
        if (formData.pan) formDataToSend.append('pan', formData.pan);
        if (formData.address) formDataToSend.append('address', formData.address);

        // Only admins/HR can modify professional details
        if (canEditProfessional) {
          if (formData.company) formDataToSend.append('company', formData.company);
          if (formData.department) formDataToSend.append('department', formData.department);
          if (formData.employeeId) formDataToSend.append('employee_id', formData.employeeId);
          if (formData.band) formDataToSend.append('band', formData.band);
          if (formData.dateOfJoining) formDataToSend.append('doj', formData.dateOfJoining);
          if (formData.dateOfLeaving) formDataToSend.append('date_of_leaving', formData.dateOfLeaving);
          if (formData.status) formDataToSend.append('status', formData.status);
          if (formData.companyLocation) formDataToSend.append('company_location', formData.companyLocation);
        }
        
        response = await axiosInstance.put(`/update-user/${userId}`, formDataToSend);
      } else {
        // No image selected, use regular JSON payload
        const updateData = applyPascalNameFields({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          gender: formData.gender,
          date_of_birth: formData.dateOfBirth,
          phone_number: formData.phoneNumber,
          emergency_contact_number: formData.emergencyContactNumber,
          aadhaar: formData.aadhaar,
          pan: formData.pan,
          address: formData.address,
          company_email: formData.companyEmail
        });

        if (canEditProfessional) {
          updateData.company = formData.company;
          updateData.department = formData.department;
          updateData.employee_id = formData.employeeId;
          updateData.band = formData.band;
          updateData.doj = formData.dateOfJoining;
          updateData.date_of_leaving = formData.dateOfLeaving;
          updateData.status = formData.status;
          updateData.company_location = formData.companyLocation;
        }

        response = await axiosInstance.put(`/update-user/${userId}`, updateData);
      }
      
      if (response.data && (response.data.status === "success" || response.status === 200 || response.data.user_id)) {
        // Update localStorage with new user data (backend returns user object directly)
        if (response.data.user_id) {
          const API_HOST = getApiUrl();
          let updatedUser = { ...response.data };
          if (updatedUser.image_url) {
            updatedUser.image_url = resolveImageUrl(updatedUser.image_url);
          }
          if (!updatedUser.image_url && updatedUser.image_path) {
            updatedUser.image_url = `${API_HOST.replace(/\/$/, "")}/uploads/cloud-office/users/${updatedUser.image_path}`;
          }
          setAuthItem("user", JSON.stringify(updatedUser));
          window.dispatchEvent(new Event('userDataUpdated'));
        } else if (response.data.data && response.data.data.user) {
          setAuthItem("user", JSON.stringify(response.data.data.user));
          window.dispatchEvent(new Event('userDataUpdated'));
        } else {
          console.error("[MyDetails] Update success but no user object in response:", response.data);
        }
        
        const resolvedImageUrl = response.data?.image_url || response.data?.data?.user?.image_url;
        setFormData(prev => ({
          ...prev,
          currentImageUrl: resolvedImageUrl
            ? resolveImageUrl(resolvedImageUrl || "")
            : prev.currentImageUrl,
          profileImage: null
        }));
        
        setIsEditing(false);
        alert("Details updated successfully!");
      } else {
        console.error("[MyDetails] Update failed – unexpected response:", response?.data, "status:", response?.status);
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("[MyDetails] Error updating details:", error?.message);
      console.error("[MyDetails] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      const errorMessage = error.response?.data?.message || error.message || "Failed to update details. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(" ") || "Your profile";
  const avatarSrc = previewUrl || formData.currentImageUrl;
  const statusTone = STATUS_TONES[String(formData.status || "").toLowerCase()] || "neutral";
  const lockNote = !canEditProfessional ? (
    <span className="md-lock-note">
      <FaLock aria-hidden="true" /> Managed by HR
    </span>
  ) : null;

  return (
    <div className="md-page" ref={topRef}>
      <button type="button" className="md-back" onClick={onBack}>
        <FaArrowLeft aria-hidden="true" /> Back
      </button>

      <form className="md-form" onSubmit={handleSubmit}>
        {error ? (
          <div className="md-banner md-banner--error" role="alert">
            <FaExclamationCircle aria-hidden="true" />
            <span>{error}</span>
            <button type="button" className="md-banner__close" onClick={() => setError("")} aria-label="Dismiss">
              <FaTimes />
            </button>
          </div>
        ) : null}
        <section className="md-hero">
          <div className="md-hero__avatar-wrap">
            {avatarSrc ? (
              <img className="md-hero__avatar" src={avatarSrc} alt={fullName} />
            ) : (
              <span className="md-hero__avatar md-hero__avatar--initials" aria-hidden="true">
                {initialsOf(formData.firstName, formData.lastName)}
              </span>
            )}
            {isEditing ? (
              <>
                <label className="md-hero__camera" htmlFor="profileImage" title="Change photo">
                  <FaCamera aria-hidden="true" />
                  <span className="md-sr-only">Change photo</span>
                </label>
                <input
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  accept="image/*"
                  onChange={handleInputChange}
                  hidden
                />
              </>
            ) : null}
          </div>
          <div className="md-hero__info">
            <div className="md-hero__name-row">
              <h1 className="md-hero__name">{fullName}</h1>
              {formData.status ? (
                <span className={`md-status md-status--${statusTone}`}>{formData.status}</span>
              ) : null}
            </div>
            {formData.role ? <p className="md-hero__role">{formData.role}</p> : null}
            <div className="md-hero__chips">
              {formData.employeeId ? (
                <span className="md-chip">
                  <FaIdBadge aria-hidden="true" /> {formData.employeeId}
                </span>
              ) : null}
              {formData.department ? (
                <span className="md-chip">
                  <FaBriefcase aria-hidden="true" /> {formData.department}
                </span>
              ) : null}
              {formData.company ? (
                <span className="md-chip">
                  <FaBuilding aria-hidden="true" /> {formData.company}
                </span>
              ) : null}
              {formData.companyLocation ? (
                <span className="md-chip">
                  <FaMapMarkerAlt aria-hidden="true" /> {formData.companyLocation}
                </span>
              ) : null}
              {formData.companyEmail || formData.email ? (
                <span className="md-chip">
                  <FaEnvelope aria-hidden="true" /> {formData.companyEmail || formData.email}
                </span>
              ) : null}
            </div>
            {formData.profileImage ? (
              <p className="md-hero__pending">
                New photo selected: <strong>{formData.profileImage.name}</strong>. Save to apply.
                <button
                  type="button"
                  className="md-link-btn"
                  onClick={() => setFormData((prev) => ({ ...prev, profileImage: null }))}
                >
                  Undo
                </button>
              </p>
            ) : null}
          </div>
          {!isEditing ? (
            <div className="md-hero__actions">
              <button type="button" className="md-btn md-btn--outline" onClick={startEditing}>
                <FaPen aria-hidden="true" /> Edit details
              </button>
            </div>
          ) : null}
        </section>

        {!isEditing ? (
          <div className="md-vgrid">
            <ViewCard
              icon={FaUser}
              tone="blue"
              title="Personal information"
              action={
                <button type="button" className="md-link-btn" onClick={startEditing}>
                  Edit
                </button>
              }
            >
              <div className="md-info-grid">
                <Info label="First name" value={formData.firstName} />
                <Info label="Last name" value={formData.lastName} />
                <Info label="Email" value={formData.email} href={formData.email ? `mailto:${formData.email}` : undefined} />
                <Info label="Gender" value={formData.gender} />
                <Info label="Date of birth" value={formatDisplayDate(formData.dateOfBirth)} />
                <Info
                  label="Phone number"
                  value={formatPhoneDisplay(formData.countryCode, formData.phoneNumber)}
                  href={phoneCallLink(formData.countryCode, formData.phoneNumber)?.href}
                />
                <Info
                  label="Emergency contact"
                  value={formatPhoneDisplay(formData.countryCode, formData.emergencyContactNumber)}
                  href={phoneCallLink(formData.countryCode, formData.emergencyContactNumber)?.href}
                />
                <Info label="Aadhaar" value={maskAadhaar(formData.aadhaar)} />
                <Info label="PAN" value={formData.pan ? String(formData.pan).toUpperCase() : ""} />
                <Info label="Address" value={formData.address} full />
              </div>
            </ViewCard>

            <ViewCard icon={FaBriefcase} tone="violet" title="Professional information" action={lockNote}>
              <div className="md-info-grid">
                <Info label="Company" value={formData.company} />
                <Info
                  label="Company email"
                  value={formData.companyEmail}
                  href={formData.companyEmail ? `mailto:${formData.companyEmail}` : undefined}
                />
                <Info label="Designation" value={formData.role} />
                <Info label="Department" value={formData.department} />
                <Info label="Employee ID" value={formData.employeeId} />
                <Info label="Band" value={formData.band} />
                <Info label="Date of joining" value={formatDisplayDate(formData.dateOfJoining)} />
                <Info label="Date of leaving" value={formatDisplayDate(formData.dateOfLeaving)} />
                <Info label="Status">
                  {formData.status ? (
                    <span className={`md-status md-status--${statusTone}`}>{formData.status}</span>
                  ) : (
                    <span className="is-empty">—</span>
                  )}
                </Info>
                <Info label="Company location" value={formData.companyLocation} />
              </div>
            </ViewCard>
          </div>
        ) : (
          <>

        <SectionCard icon={FaUser} title="Personal Information" subtitle="Your contact and identity details.">
          <div className="md-grid">
            <Field label="First Name">
              <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} />
            </Field>
            <Field label="Last Name" required>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
            </Field>
            <Field label="Email">
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
            </Field>
            <Field label="Gender">
              <select name="gender" value={formData.gender} onChange={handleInputChange}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </Field>
            <Field label="Date of Birth">
              <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} />
            </Field>
            <Field label="Phone Number">
              <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} />
            </Field>
            <Field label="Emergency Contact Number">
              <input
                type="tel"
                name="emergencyContactNumber"
                value={formData.emergencyContactNumber}
                onChange={handleInputChange}
              />
            </Field>
            <Field label="Aadhaar">
              <input type="text" name="aadhaar" value={formData.aadhaar} onChange={handleInputChange} maxLength="12" />
            </Field>
            <Field label="PAN">
              <input type="text" name="pan" value={formData.pan} onChange={handleInputChange} maxLength="10" />
            </Field>
            <Field label="Address" full>
              <textarea name="address" value={formData.address} onChange={handleInputChange} rows="3" />
            </Field>
          </div>
        </SectionCard>

        <SectionCard
          icon={FaBriefcase}
          title="Professional Information"
          subtitle="Employment details on record."
          aside={lockNote}
        >
          <div className="md-grid">
            <Field label="Company">
              <input type="text" name="company" value={formData.company} onChange={handleInputChange} disabled={!canEditProfessional} />
            </Field>
            <Field label="Company Email">
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </Field>
            <Field label="Designation">
              <input type="text" name="role" value={formData.role} readOnly disabled />
            </Field>
            <Field label="Department">
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </Field>
            <Field label="Employee ID">
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </Field>
            <Field label="Band">
              <select name="band" value={formData.band} onChange={handleInputChange} disabled={!canEditProfessional}>
                <option value="">Select Band</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
              </select>
            </Field>
            <Field label="Date of Joining">
              <input
                type="date"
                name="dateOfJoining"
                value={formData.dateOfJoining}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </Field>
            <Field label="Date of Leaving">
              <input
                type="date"
                name="dateOfLeaving"
                value={formData.dateOfLeaving}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </Field>
            <Field label="Status">
              <select name="status" value={formData.status} onChange={handleInputChange} disabled={!canEditProfessional}>
                <option value="">Select Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Resigned">Resigned</option>
                <option value="Terminated">Terminated</option>
                <option value="On Hold">On Hold</option>
              </select>
            </Field>
            <Field label="Company Location">
              <input
                type="text"
                name="companyLocation"
                value={formData.companyLocation}
                onChange={handleInputChange}
                disabled={!canEditProfessional}
              />
            </Field>
          </div>
        </SectionCard>

        <div className="md-actions">
          <span className="md-actions__hint">
            {formData.profileImage ? "Photo and details will be saved together." : "Review your changes before saving."}
          </span>
          <div className="md-actions__buttons">
            <button type="button" className="md-btn md-btn--ghost" onClick={cancelEditing} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="md-btn md-btn--primary" disabled={loading}>
              {loading ? "Updating…" : "Update"}
            </button>
          </div>
        </div>
          </>
        )}

        {!isEditing ? (
        <SectionCard
          icon={FaShieldAlt}
          title="Assigned Modules & Permissions"
          subtitle={
            loadingPerms
              ? "Loading your access…"
              : permissionModules.length
                ? `${permissionModules.length} modules · ${totalPermissions} permissions`
                : "Access granted through your role."
          }
          aside={
            permissionModules.length > 0 ? (
              <div className="md-search">
                <FaSearch aria-hidden="true" />
                <input
                  type="search"
                  value={permQuery}
                  onChange={(e) => setPermQuery(e.target.value)}
                  placeholder="Search modules or permissions"
                  aria-label="Search modules or permissions"
                />
              </div>
            ) : null
          }
        >
          {loadingPerms ? (
            <div className="md-perm-grid" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="md-perm-card md-perm-card--skeleton" />
              ))}
            </div>
          ) : permissionModules.length === 0 ? (
            <div className="md-empty">
              <FaShieldAlt aria-hidden="true" />
              <span>No modules or permissions assigned.</span>
            </div>
          ) : visibleModules.length === 0 ? (
            <div className="md-empty">
              <FaSearch aria-hidden="true" />
              <span>No modules match “{permQuery}”.</span>
            </div>
          ) : (
            <div className="md-perm-grid">
              {visibleModules.map((m) => (
                <div key={m.name} className="md-perm-card">
                  <div className="md-perm-card__head">
                    <span className="md-perm-card__name">{m.name}</span>
                    <span className="md-perm-card__count">{m.perms.length}</span>
                  </div>
                  <div className="md-perm-card__chips">
                    {m.perms.map((p) => {
                      const chip = permissionChip(p);
                      return (
                        <span key={p} className={`md-perm-chip md-perm-chip--${chip.tone}`} title={chip.title}>
                          {chip.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        ) : null}
      </form>
    </div>
  );
}

export default MyDetails;
